import React, { useEffect, useLayoutEffect, useRef, useState } from 'react';

import { useSiteActions } from '../../store/actionContext.ts';
import { normalizeStickers } from './presets.js';
import { Sticker } from './Sticker.jsx';
import { StickerLayerRoot } from './stickerLayer.styled.js';

// Profile sticker overlay. Rendered inside DesignSiteContainer in both Dashboard
// (preview, editable) and Next (live site, read-only). Editing affordances come
// from SiteActionContext: `editStickers` is only true in the Dashboard sticker
// mode, so on Next the layer is purely presentational.
export const StickerLayer = ({ stickers }) => {
  const { editStickers, onUpdateSticker, onDeleteSticker } = useSiteActions();
  const list = normalizeStickers(stickers);

  const rootRef = useRef(null);
  const [layerWidth, setLayerWidth] = useState(0);
  const [selectedId, setSelectedId] = useState(null);

  useLayoutEffect(() => {
    const el = rootRef.current;
    if (!el) return undefined;
    const measure = () => setLayerWidth(el.getBoundingClientRect().width);
    measure();
    if (typeof ResizeObserver === 'undefined') return undefined;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Click anywhere that isn't a sticker → deselect (only matters in edit mode).
  useEffect(() => {
    if (!editStickers || selectedId == null) return undefined;
    const onDocDown = (e) => {
      if (!e.target.closest || !e.target.closest('[data-sticker-id]')) setSelectedId(null);
    };
    document.addEventListener('pointerdown', onDocDown, true);
    return () => document.removeEventListener('pointerdown', onDocDown, true);
  }, [editStickers, selectedId]);

  // Re-stack a sticker by adjusting its `z` relative to its siblings (front/back jump to
  // the extremes, forward/backward step by one). Persisted via onUpdateSticker like any field.
  const applyLayer = (id, direction) => {
    const zs = list.map((s) => s.z || 0);
    const curZ = list.find((s) => s.id === id)?.z || 0;
    let nz = curZ;
    if (direction === 'front') nz = Math.max(0, ...zs) + 1;
    else if (direction === 'back') nz = Math.min(0, ...zs) - 1;
    else if (direction === 'forward') nz = curZ + 1;
    else if (direction === 'backward') nz = curZ - 1;
    if (nz !== curZ) onUpdateSticker?.(id, { z: nz });
  };

  if (!list.length && !editStickers) return null;

  return (
    <StickerLayerRoot ref={rootRef} data-sticker-layer>
      {list.map((sticker) => (
        <Sticker
          key={sticker.id}
          sticker={sticker}
          layerWidth={layerWidth}
          editable={!!editStickers}
          selected={!!editStickers && selectedId === sticker.id}
          onSelect={() => setSelectedId(sticker.id)}
          onChange={(patch) => onUpdateSticker?.(sticker.id, patch)}
          onLayer={(direction) => applyLayer(sticker.id, direction)}
          onDelete={() => {
            onDeleteSticker?.(sticker.id);
            setSelectedId(null);
          }}
        />
      ))}
    </StickerLayerRoot>
  );
};
